import { useMemo } from 'react';
import { Target, Source, Destination, OptimizelyProduct } from '../types/DataSyncItem';
import { DataSyncObjectsStore } from '../stores/object/DataSyncObjectsStore';
import { DataSyncObjectStoreFactory } from '../stores/object/DataSyncObjectStoreFactory';
import { useInstanceAvailability } from './useAvailabilityCheck';

/**
 * Hook for accessing DataSyncObjectsStore instances within React components
 * This abstracts away the store creation logic from components and includes instance availability checking
 *
 * @param target The Target (SOURCE or DESTINATION) for which the store is created
 * @param object Optional Source or Destination object to determine the product and instance
 * @returns A DataSyncObjectsStore instance for the specified product and instance, or null if unavailable
 */
export function useDataSyncObjectStore<T extends DataSyncObjectsStore>(
  target: Target,
  object?: Source | Destination,
): T | null {

  const instanceStatus = useInstanceAvailability({
    product: object?.product as OptimizelyProduct,
    instanceId: object?.instance?.id,
    target,
  });

  return useMemo(() => {
    if (!object?.product) {
      return null;
    }

    // OCP Database (HUB) doesn't require an instance selection
    const requiresInstance = object.product !== OptimizelyProduct.HUB;

    if (requiresInstance && !object?.instance?.id) {
      return null;
    }

    // Only create store when instance is available (not loading or unavailable)
    if (requiresInstance && instanceStatus !== 'available') {
      return null;
    }

    return DataSyncObjectStoreFactory.createStore(object.product, target, object.instance.id) as T;
  }, [target, object?.product, object?.instance?.id, instanceStatus]);
}
